/**
 * Button.tsx — Generic Button block + Share-action variant
 *
 * the share-action variant supports responsive `iconOnly`
 * and `showText` props via the `{ d, t, m }` shape, matching WishlistBtn /
 * CompareButton. Generic link-buttons are unchanged.
 */
import React, { useId, useState } from 'react';

type ResponsiveBool = boolean | { d?: boolean; t?: boolean; m?: boolean };

interface ButtonProps {
  data: {
    text?: string;
    label?: string;
    link?: string;
    style?: 'primary' | 'secondary' | 'outline';
    size?: 'small' | 'medium' | 'large';
    fullWidth?: boolean;
    openInNewTab?: boolean;
    icon?: string;
    iconPosition?: 'left' | 'right';
    action?: string;
    variant?: string;
    showText?: ResponsiveBool;
    iconOnly?: ResponsiveBool;
    [key: string]: unknown;
  };
  styles?: string;
  context?: unknown;
}

function resolveResponsiveBool(val: ResponsiveBool | undefined, fallback: boolean): { d: boolean; t: boolean; m: boolean } {
  if (val == null) return { d: fallback, t: fallback, m: fallback };
  if (typeof val === 'boolean') return { d: val, t: val, m: val };
  const d = val.d ?? fallback;
  const t = val.t ?? d;
  const m = val.m ?? t;
  return { d, t, m };
}

export const Button: React.FC<ButtonProps> = ({ data, styles }) => {
  const {
    text,
    label,
    link = '#',
    style = 'primary',
    size = 'medium',
    fullWidth = false,
    openInNewTab = false,
    icon,
    iconPosition = 'left',
    action,
  } = data ?? {};

  const displayText = text || label || 'Button';
  const [shareLabel, setShareLabel] = useState(displayText);
  // Hooks must be called unconditionally — pulled to top regardless of action
  // so React's hook order is stable across renders. The id is only USED in
  // the share branch; callers paying for it elsewhere is negligible.
  const reactId = useId();

  // Share Action: Compact PDP-style button
  if (action === 'share') {
    const cssClass = `sb-${reactId.replace(/[:]/g, '')}`;
    const showTextResolved = resolveResponsiveBool(data?.showText, true);
    const iconOnlyResolved = resolveResponsiveBool(data?.iconOnly, false);
    const labelVisibleD = !iconOnlyResolved.d && showTextResolved.d;
    const labelVisibleT = !iconOnlyResolved.t && showTextResolved.t;
    const labelVisibleM = !iconOnlyResolved.m && showTextResolved.m;
    const generatedCss = `
      .${cssClass} .sb-label { display: ${labelVisibleD ? 'inline' : 'none'}; }
      @media (min-width: 769px) and (max-width: 1024px) { .${cssClass} .sb-label { display: ${labelVisibleT ? 'inline' : 'none'}; } }
      @media (max-width: 768px) { .${cssClass} .sb-label { display: ${labelVisibleM ? 'inline' : 'none'}; } }
    `;

    const handleShare = async () => {
      const url = window.location.href;
      const title = document.title;
      if (navigator.share) {
        try { await navigator.share({ title, url }); } catch { /* cancelled */ }
      } else {
        try {
          await navigator.clipboard.writeText(url);
          setShareLabel('Copied!');
          setTimeout(() => setShareLabel(displayText), 2000);
        } catch { /* ignore */ }
      }
    };

    return (
      <div className={styles ?? ''}>
        <style dangerouslySetInnerHTML={{ __html: generatedCss }} />
        <button
          onClick={() => { void handleShare(); }}
          aria-label={shareLabel}
          className={`${cssClass} w-full flex items-center justify-center gap-1.5 px-2 py-2.5 text-[11px] font-medium transition-all
            border border-[var(--glass-border,rgba(255,255,255,0.08))]
            text-[var(--text-muted,#94a3b8)]
            hover:text-[var(--text-primary,#f1f5f9)]
            hover:border-[var(--accent-secondary,#f97316)]/30`}
          style={{ borderRadius: 'var(--style-btn-radius, 12px)' }}
        >
          <svg className="w-3.5 h-3.5 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
          </svg>
          <span className="sb-label truncate">{shareLabel}</span>
        </button>
      </div>
    );
  }

  // Default Link Button
  // Colour comes from the active theme/style preset via CTA tokens — never a
  // hardcoded palette. primary = filled CTA, secondary = surface, outline =
  // bordered accent. Per the 3-preset model the merchant changes the look by
  // swapping presets, not by editing the button.
  const styleStyles: Record<string, React.CSSProperties> = {
    primary: {
      backgroundColor: 'var(--color-cta-primary-bg, var(--accent-button-strong, var(--accent-primary)))',
      color: 'var(--color-cta-primary-text, var(--color-button-text, #ffffff))',
    },
    secondary: {
      backgroundColor: 'var(--color-cta-secondary-bg, var(--surface-2))',
      color: 'var(--color-cta-secondary-text, var(--text-primary))',
    },
    outline: {
      backgroundColor: 'transparent',
      color: 'var(--color-accent-fg, var(--accent-primary))',
      border: '2px solid var(--color-accent-fg, var(--accent-primary))',
    },
  };

  const sizeClasses = {
    small: 'px-3 py-1 text-sm',
    medium: 'px-4 py-2',
    large: 'px-6 py-3 text-lg'
  };

  return (
    <div className="">
      <a
        href={link}
        target={openInNewTab ? '_blank' : undefined}
        rel={openInNewTab ? 'noopener noreferrer' : undefined}
        className={`inline-flex items-center gap-2 font-medium transition-opacity hover:opacity-90 ${sizeClasses[size]} ${fullWidth ? 'w-full justify-center' : ''}`}
        style={{ borderRadius: 'var(--style-btn-radius, 8px)', ...styleStyles[style] }}
      >
        {icon && iconPosition === 'left' && <span>{icon}</span>}
        {displayText}
        {icon && iconPosition === 'right' && <span>{icon}</span>}
      </a>
    </div>
  );
};
